import {
  KPIStrip,
  KPIStripItem,
  KPIStripItemLabel,
  KPIStripItemValue,
} from "@/components/ui/kpi-strip";
import { TrendingDown, TrendingUp } from "lucide-react";
import { formatQuantity } from "@/lib/quantity-format";
import type { ReportIndicator, ReportStage } from "@/types/production-report.types";

// Spec 083 (T033) — os indicadores do topo de uma etapa.
//
// Os quatro primeiros cartões NÃO são conceitos novos: são a projeção de um indicador declarado
// pela organização. Entrada é o denominador, saída útil é o numerador, o valor é o aproveitamento e
// a quebra é a diferença. É por isso que nenhum deles virou campo no modelo.
//
// Sem indicador eleito, a faixa mostra só o que existe sem ele: entrada, refugo e retrabalho.

interface Props {
  stage: ReportStage;
  hero: ReportIndicator | null;
}

const pct = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 });

function Valor({ children }: { children: React.ReactNode }) {
  return <KPIStripItemValue>{children}</KPIStripItemValue>;
}

/** "Sem base" é TEXTO, nunca `0%`: zero afirmaria que a fábrica não aproveitou nada. */
function ValorIndicador({ ind }: { ind: ReportIndicator }) {
  if (ind.value === null) {
    return (
      <span className="text-[13px] text-text-secondary">
        {ind.reason === "ZERO_BASE" ? "base zerada" : "sem base"}
      </span>
    );
  }
  return <Valor>{pct.format(ind.value)}%</Valor>;
}

/** Seta, e não bolinha: a bolinha colorida diz "algo mudou", a seta diz PARA QUAL LADO sem cor. */
function Delta({ ind }: { ind: ReportIndicator }) {
  if (ind.deltaPp === null) return <span className="text-[11px] text-text-tertiary">—</span>;
  const sobe = ind.deltaPp >= 0;
  const Seta = sobe ? TrendingUp : TrendingDown;
  return (
    <span className="flex items-center gap-1.5 text-[11px] text-text-secondary">
      <Seta size={12} aria-hidden className={sobe ? "text-rd-success-600" : "text-rd-danger-600"} />
      <span className={sobe ? "font-medium text-rd-success-700" : "font-medium text-rd-danger-700"}>
        {sobe ? "+" : ""}
        {pct.format(ind.deltaPp)} p.p.
      </span>{" "}
      vs. período anterior
    </span>
  );
}

export function StageKpiRow({ stage, hero }: Props) {
  const unidade = stage.unitIsEquivalent ? `${stage.unit}-eq` : stage.unit;
  const refugo = stage.losses
    .filter((l) => l.lossType !== "REWORK")
    .reduce((acc, l) => acc + l.quantity, 0);
  const retrabalho = stage.losses
    .filter((l) => l.lossType === "REWORK")
    .reduce((acc, l) => acc + l.quantity, 0);
  const quebra =
    hero && hero.denominator !== null && hero.numerator !== null
      ? hero.denominator - hero.numerator
      : null;
  // Base zerada não vira 0%: dividir por zero aqui afirmaria "não quebrou nada" onde não houve o
  // que medir — o mesmo erro que a tela recusa no indicador.
  const quebraPct =
    quebra !== null && hero && hero.denominator !== null && hero.denominator > 0
      ? (quebra / hero.denominator) * 100
      : null;

  return (
    <KPIStrip className="grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
      <KPIStripItem>
        <KPIStripItemLabel>Entrada</KPIStripItemLabel>
        <Valor>{formatQuantity(stage.input.quantity)}</Valor>
        <span className="text-[11px] text-text-tertiary">
          {stage.input.quantity === null ? "sem registro" : stage.unit}
        </span>
      </KPIStripItem>

      <KPIStripItem accent={hero !== null}>
        <KPIStripItemLabel>Saída útil</KPIStripItemLabel>
        <Valor>{formatQuantity(hero?.numerator ?? null)}</Valor>
        <span className="text-[11px] text-text-tertiary">{unidade}</span>
      </KPIStripItem>

      <KPIStripItem>
        <KPIStripItemLabel>{hero ? hero.name : "Aproveitamento"}</KPIStripItemLabel>
        {hero ? <ValorIndicador ind={hero} /> : <Valor>—</Valor>}
        {hero ? <Delta ind={hero} /> : null}
      </KPIStripItem>

      {/* Os dois números juntos porque eles respondem perguntas diferentes: o percentual diz se a
          quebra está no normal da linha, o peso diz quanto alho isso é. Um sem o outro manda o
          gestor fazer a conta de cabeça. */}
      <KPIStripItem>
        <KPIStripItemLabel>Quebra</KPIStripItemLabel>
        {quebraPct === null ? (
          <Valor>{formatQuantity(quebra)}</Valor>
        ) : (
          <span className="flex min-w-0 items-baseline gap-2">
            <Valor>{pct.format(quebraPct)}%</Valor>
            <span className="truncate font-mono text-[13px] tabular-nums text-text-secondary">
              {formatQuantity(quebra)} {stage.unit}
            </span>
          </span>
        )}
        <span className="text-[11px] text-text-tertiary">casca e refile · fora de A × P × Q</span>
      </KPIStripItem>

      <KPIStripItem>
        <KPIStripItemLabel>Refugo</KPIStripItemLabel>
        <Valor>{stage.losses.length === 0 ? "—" : formatQuantity(refugo)}</Valor>
        <span className="text-[11px] text-text-tertiary">entra na Qualidade</span>
      </KPIStripItem>

      <KPIStripItem>
        <KPIStripItemLabel>Retrabalho</KPIStripItemLabel>
        <Valor>{stage.losses.length === 0 ? "—" : formatQuantity(retrabalho)}</Valor>
        <span className="text-[11px] text-text-tertiary">reprocessado</span>
      </KPIStripItem>
    </KPIStrip>
  );
}
